import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisToasterService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
} from '@campaign-portal/components-library';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { CreateResponse, ReadResponse, UpdateResponse } from '@campaign-portal/namespace/common/implementations';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { LinkDomain } from '@campaign-portal/namespace/entities/link-domains/specs';
import { LinkDomainStatus } from '@campaign-portal/namespace/common/enums';
import { LinkDomainEntity, SslDomainStatus } from '../types';
import { RPCResult } from '@campaign-portal/namespace/common/rpc.response';
import { ContractCompany } from '@campaign-portal/namespace/entities/contract-companies/specs';

type LinkDomainApiResponse = LinkDomain<exist>;

@Injectable({
	providedIn: 'root',
})
export class LinkDomainsService
	extends ExtendableRefBookService<LinkDomainEntity<exist>>
	implements AbstractCRUDService
{
	readonly create = this.update;
	activeLinkDomainsList$ = new BehaviorSubject<LinkDomainEntity<exist>[]>([]);

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService
	) {
		super();
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.shortDomain');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.urlShortener');
	}

	override load(): Observable<ReadResponse<LinkDomainEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<LinkDomainApiResponse[]>>('LinkDomains.Read', {}, this.loading$, this.errorNotifier)
			.pipe(
				map((resp) => {
					const entities = resp.Data.map((d) => this.toEntity(d));
					this.activeLinkDomainsList$.next(entities.filter((link) => link.isActive()));

					return super.process({ ...resp, Data: entities });
				})
			);
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<LinkDomainEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<LinkDomainApiResponse[]>>('LinkDomains.Read', params, this.loading$, this.errorNotifier)
			.pipe(map((resp) => ({ ...resp, Data: resp.Data.map((d) => this.toEntity(d)) })));
	}

	toEntity(domain: LinkDomainApiResponse): LinkDomainEntity<exist> {
		return new LinkDomainEntity(domain);
	}

	update(domain: LinkDomain): Observable<CreateResponse<LinkDomain>> {
		const notify = (response: CreateResponse<LinkDomain>): void => {
			if (response.Success) {
				const message = this.langService.translate(
					domain.id ? 'notifications.actions.update' : 'notifications.actions.create'
				);
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api
			.loader<
				CreateResponse<LinkDomain>
			>(`LinkDomains.${domain.id ? 'Update' : 'Create'}`, { Data: { Entities: [domain] } }, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	delete(domain: LinkDomain<exist>): Observable<UpdateResponse<LinkDomain>> {
		const notify = (response: CreateResponse<LinkDomain>): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.delete');
				this.alarisToaster.success(message, this.title);
			}
		};
		const _domain = {
			...domain,
			status: LinkDomainStatus.ARCHIVED,
		};
		return this.api
			.loader<
				CreateResponse<LinkDomain<exist>>
			>(`LinkDomains.Update`, { Data: { Entities: [_domain] } }, this.loading$, this.errorNotifier, notify)
			.pipe(
				tap(() => {
					this.refresh$.next();
				})
			);
	}

	getSslStatus(domain: LinkDomain): SslDomainStatus {
		return new LinkDomainEntity(domain as LinkDomainApiResponse).getSslStatus();
	}

	checkContractCompanyUsage(id: Id<exist>): Observable<RPCResult<ContractCompany<exist>[]>> {
		return this.api.loader<RPCResult<ContractCompany<exist>[]>>(
			'ContractCompanies.CheckDomainUsage',
			{ Data: { Ids: [id] } },
			this.loading$,
			this.errorNotifier
		);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });
}
